import { useCallback, useState } from 'react'

import { Volume } from '@/api/type-server'

// Mount config options for different drive types
export interface MountConfigOptions {
  defaultPath: string
  defaultReadWriteMode: boolean
}

// Pre-defined configurations
export const PERSONAL_MOUNT_CONFIG: MountConfigOptions = {
  defaultPath: '/datadrive',
  defaultReadWriteMode: false,
}

export const TEAM_MOUNT_CONFIG: MountConfigOptions = {
  defaultPath: '/team_data',
  defaultReadWriteMode: false,
}

// Generic mount config hook
export function useMountConfig<T extends Volume = Volume>(
  options: MountConfigOptions = PERSONAL_MOUNT_CONFIG,
) {
  const { defaultPath, defaultReadWriteMode } = options

  const [mountVolume, setMountVolume] = useState<T | undefined>()
  const [mountPath, setMountPath] = useState<string>(defaultPath)
  const [readWriteMode, setReadWriteMode] = useState<boolean>(defaultReadWriteMode)

  const updateMountConfig = useCallback(
    (volume?: T, path: string = defaultPath, mode: boolean = defaultReadWriteMode) => {
      setMountVolume(volume)
      setMountPath(path)
      setReadWriteMode(mode)
    },
    [defaultPath, defaultReadWriteMode],
  )

  const resetMountConfig = useCallback(() => {
    setMountVolume(undefined)
    setMountPath(defaultPath)
    setReadWriteMode(defaultReadWriteMode)
  }, [defaultPath, defaultReadWriteMode])

  return {
    mountVolume,
    setMountVolume,
    mountPath,
    setMountPath,
    readWriteMode,
    setReadWriteMode,
    updateMountConfig,
    resetMountConfig,
  }
}
